import { useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { CircularProgress } from "@mui/material";

import { Icon } from "@/components";
import { Button } from "@components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import DeleteModal from "@/components/Modals/DeleteModal";

import {
	useCreateIndustryMutation,
	useDeleteIndustryMutation,
} from "@/pages/Private/redux/industry/industry.api";

import EditableIndustryItem from "./EditableIndustryItem";

import {
	CreateIndustry,
	CreateIndustrySchema,
	Industry,
	IndustryArray,
} from "../industry/schema/industry";
import EditModeIndustryItem from "./EditableIndustryItem/EditModeIndustryItem";

interface EditableIndustryListProps {
	data?: IndustryArray;
	isLoading: boolean;
	editingIndustryId: number;
	setEditingIndustryId: (value: number) => void;
}

export default function EditableIndustryList({
	data,
	isLoading,
	editingIndustryId,
	setEditingIndustryId,
}: EditableIndustryListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-industries.${key}`, options);
	const [isCreating, setCreating] = useState(0);

	const [deletingIndustry, setDeletingIndustry] = useState<Industry | null>(null);
	const [createIndustry, { isLoading: isCreatingIndustry }] = useCreateIndustryMutation();
	const [deleteIndustry, { isLoading: isIndustryDeleting }] = useDeleteIndustryMutation();

	const handleCreateIndustry = async (values: CreateIndustry) => {
		try {
			await createIndustry(values);
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(0);
		}
	};

	const handleDeleteIndustry = async () => {
		if (!deletingIndustry?.id) {
			return;
		}

		try {
			await deleteIndustry(deletingIndustry?.id ?? 0).unwrap();
			setDeletingIndustry(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditIndustry = (id: number) => {
		setEditingIndustryId(id);
		setCreating(0);
	};

	const handleCancel = () => {
		setEditingIndustryId(-1);
		setCreating(0);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-[#F7F8FB]`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("industries")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(1);
							if (editingIndustryId !== -1) {
								setEditingIndustryId(-1);
							}
						}}
					/>
				</div>
				{isLoading ? (
					<div className="w-full flex justify-center py-10">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 relative">
						{isIndustryDeleting && (
							<div className="w-full h-full absolute flex justify-center py-12 bg-white opacity-50">
								<CircularProgress className="!w-12 !h-12" />
							</div>
						)}
						{isCreating === 1 && (
							<EditModeIndustryItem
								data={{ name: "" }}
								isLoading={isCreatingIndustry}
								setEditing={handleCancel}
								validationSchema={CreateIndustrySchema}
								onSubmit={handleCreateIndustry}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableIndustryItem
									key={item?.id}
									data={item}
									isEditing={editingIndustryId === item?.id}
									isEditingInProcess={isCreating !== 0 || editingIndustryId !== -1}
									setDeletingIndustry={setDeletingIndustry}
									setEditing={onEditIndustry}
								/>
							))
						) : (
							<p className="text-[16px] text-[#777D8D] text-center py-4">{ts("list-empty")}</p>
						)}

						{isCreating === -1 && (
							<EditModeIndustryItem
								data={{ name: "" }}
								isLoading={isCreatingIndustry}
								setEditing={handleCancel}
								validationSchema={CreateIndustrySchema}
								onSubmit={handleCreateIndustry}
							/>
						)}

						<Button
							className="!text-[14px] !m-3 !mt-4 !w-fit !font-medium leading-6"
							image={<Icon className="text-white w-5 h-5 mr-2" icon={faPlus} />}
							title={ts("add-industry")}
							type="button"
							onClick={() => {
								setCreating(-1);
								if (editingIndustryId !== -1) {
									setEditingIndustryId(-1);
								}
							}}
						/>
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { taskTitle: deletingIndustry?.name })}
				handleClose={() => setDeletingIndustry(null)}
				handleDelete={handleDeleteIndustry}
				isLoading={isIndustryDeleting}
				isShowing={!!deletingIndustry}
				title={ts("delete-title")}
			/>
		</DndProvider>
	);
}
